import { useProjectParams } from '@hooks/useProjectParams';
import { ControlledSelectField, ControlNumberField } from 'components/Form';
import { OtherManualSeasonDistribution } from 'components/OtherManualSeasonDistribution';
import type { ConstructionTypeData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useUerContractSeasons } from 'pages/Project/Common/Contract/hooks/useUerContractSeasons';
import { seasonItems } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { validateLimitCost } from 'pages/Project/Common/hooks/prepareLimitsData';
import type { IUerContractOtherCostLimitSpanSelectionData } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { Checkbox, FieldGroup, Grid, GridItem } from 'uikit';

import cn from 'classnames';
import type React from 'react';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormClearErrors, UseFormSetValue } from 'react-hook-form/dist/types/form';

import { ContractOtherCostLimitSpan } from '../ContractOtherCostLimitSpan/ContractOtherCostLimitSpan';

import Styles from './EditOtherForm.module.css';

interface IEditOtherLimits {
    control: Control<any>;
    workOrderId?: number;
    selectedConstructionType: Record<number, boolean>;
    changeSelectedConstructionTypeHandler: (id: number, newState: boolean) => void;
    currentConstructionTypeId: number;
    areFieldsDisabled: boolean;
    constructionTypes?: ConstructionTypeData[];
    isTooltipHidden?: boolean;
    costRounding: number;
    clearErrors?: UseFormClearErrors<any>;
    setValue: UseFormSetValue<any>;
    noLimitSpan?: boolean;
    spanSelectionData?: IUerContractOtherCostLimitSpanSelectionData;
    isCalculationModal?: boolean;
}

export const EditOtherLimits: React.FC<IEditOtherLimits> = (props) => {
    const {
        control,
        selectedConstructionType,
        changeSelectedConstructionTypeHandler,
        currentConstructionTypeId,
        areFieldsDisabled,
        constructionTypes,
        isTooltipHidden,
        costRounding,
        clearErrors,
        setValue,
        noLimitSpan,
        spanSelectionData,
        isCalculationModal = false,
    } = props;

    const { id: projectId, methodology, orderId } = useProjectParams();

    const { projectData } = useProjectData(projectId, false);

    const isMethodologyUER = methodology?.toUpperCase() === ProjectMethodologyEnum.Uer;
    const isMethodologyBIM = methodology?.toUpperCase() === ProjectMethodologyEnum.Bim;

    const constructionTypeCheckHandler = (params: { checked: boolean }, id: number) => {
        changeSelectedConstructionTypeHandler(id, params.checked);
    };

    const [contractSeasons] = useUerContractSeasons(projectData?.contractId, Boolean(orderId));

    const constructionTypesSortName = useMemo(
        () => (constructionTypes ? [...constructionTypes].sort((a, b) => a?.name?.localeCompare(b?.name)) : []),
        [constructionTypes],
    );

    const currentName = useWatch({ control, name: 'name' });

    return (
        <Grid>
            <GridItem
                className={cn({
                    [Styles.labelBlock]: isMethodologyUER,
                    [Styles.bimLabelBlock]: isMethodologyBIM,
                    [Styles.calculationLabelBlock]: isCalculationModal,
                })}
            >
                <span>Вид строительства</span>
                <span
                    className={cn({ [Styles.priceLabel]: isMethodologyUER, [Styles.priceLabel_bim]: isMethodologyBIM })}
                >
                    Сумма, руб. без НДС
                </span>
                {isMethodologyUER && !isCalculationModal && (
                    <span className={Styles.typeLabel}>Способ распределения</span>
                )}
            </GridItem>
            {constructionTypesSortName?.map(({ id, name }) => {
                const defaultConstruction = id === currentConstructionTypeId;
                const isCheckDisabled = defaultConstruction || areFieldsDisabled;
                const isChecked = selectedConstructionType[id] ?? false;
                const isDisabled = areFieldsDisabled || !isChecked;
                const rules = {
                    validate: validateLimitCost({
                        isSelected: !isDisabled,
                    }),
                };
                const label = (
                    <Checkbox
                        id={String(id)}
                        onChange={(params) => constructionTypeCheckHandler(params, id)}
                        checked={isChecked}
                        disabled={isCheckDisabled}
                        style={{ marginRight: 10, marginTop: 5, cursor: 'pointer' }}
                        label={name}
                    />
                );

                return (
                    <GridItem key={id} className={Styles.marginBlock}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {isMethodologyBIM && (
                                <ControlNumberField
                                    name={`cost.${id}`}
                                    label={label as any}
                                    labelPosition="left"
                                    control={control}
                                    type="text"
                                    className={cn({
                                        [Styles.textInput]: isMethodologyUER,
                                        [Styles.w_100]: isMethodologyBIM,
                                    })}
                                    size="s"
                                    required={(!isDisabled && defaultConstruction) || isChecked}
                                    rules={rules}
                                    disabled={isDisabled}
                                    clearOnDisabled={!isChecked}
                                    isTooltipHidden={isTooltipHidden}
                                    decimalScale={isMethodologyBIM ? 0 : costRounding || 0}
                                    placeholder={isDisabled ? '' : 'Введите'}
                                />
                            )}
                            {isMethodologyUER && (
                                <>
                                    <div
                                        className={Styles.wrapperContractLimitSpan}
                                        style={isCalculationModal ? { minWidth: '100%' } : { marginRight: '16px' }}
                                    >
                                        <ContractOtherCostLimitSpan
                                            label={label}
                                            control={control}
                                            costRounding={costRounding}
                                            defaultConstruction={defaultConstruction}
                                            constructionTypeId={id}
                                            constructionTypeName={name}
                                            isDisabled={isDisabled || isCalculationModal}
                                            isChecked={isChecked}
                                            isTooltipHidden={!!isTooltipHidden}
                                            setValue={setValue}
                                            noLimitSpan={noLimitSpan}
                                            name={currentName}
                                            spanSelectionData={spanSelectionData}
                                            inputClassName={
                                                isCalculationModal
                                                    ? Styles.contractOtherCostLimitNoSpanInput
                                                    : Styles.styleInputCostLimit
                                            }
                                            isFullWidth={isCalculationModal}
                                            isEmptyPlaceholder={isCalculationModal}
                                            notRequired={isCalculationModal}
                                        />
                                    </div>
                                    {!isCalculationModal && (
                                        <FieldGroup size="s" className={Styles.seasonSelect}>
                                            <ControlledSelectField
                                                name={`method.${id}`}
                                                control={control}
                                                items={seasonItems}
                                                className={Styles.seasonSelect}
                                                required={defaultConstruction}
                                                rules={rules}
                                                disabled={isDisabled}
                                                clearOnDisabled={!isChecked}
                                                isTooltipHidden={isTooltipHidden}
                                                tooltipDirection="downCenter"
                                                placeholder={isDisabled ? '' : 'Выберите'}
                                            />
                                            <OtherManualSeasonDistribution
                                                control={control}
                                                constructionTypeName={name}
                                                constructionTypeId={id}
                                                costRounding={costRounding ?? 0}
                                                isDisabled={isDisabled}
                                                clearErrors={clearErrors}
                                                seasons={contractSeasons}
                                            />
                                        </FieldGroup>
                                    )}
                                </>
                            )}
                        </div>
                    </GridItem>
                );
            })}
        </Grid>
    );
};

EditOtherLimits.displayName = 'EditOtherLimits';
